import { CodedProperty, NamedProperty, uniqBy } from "sprint-common-web";

import { CLEAN_VIDEO_SUBTITLE_LANGUAGE_ID } from "../../../common/api/master-api/common/consts";
import {
    OrderRequestDetailsAssetsResponse,
    OrderRequestDetailsResponseItem,
    OrderRequestDetailsSubtitleResponse,
    OrderRequestDetailsVideoResponse
} from "../../../common/api/orders-api/order-request/GetOrderRequestDetailsResponse";
import {
    OrderRequestListItemResponse,
    OrderRequestSoundtrackResponse,
    OrderRequestSubtitleResponse,
    OrderRequestVideoResponse
} from "../../../common/api/orders-api/order-request/GetOrderRequestListResponse";
import {
    resolveOrderContentTitle,
    resolveOrderContentVersion
} from "../../../common/api/orders-api/order/ContentReference";
import { ContentReferenceType } from "../../../common/types/ContentReferenceType";
import { DynamicSubtitleType } from "../../../common/types/DynamicSubtitleType";
import { OrderRequestStatus } from "../../../common/types/OrderRequestStatus";
import { LanguageReference } from "../../../common/types/references/LanguageReference";
import { OrderAssetAvailability } from "../../common/types/OrderAssetAvailability";
import { OrderResolutionDimensions } from "../../common/types/OrderResolutionDimensions";
import { calculateOrderProgress, OrderProgressData } from "../../common/utils/calculateOrderProgress";

export interface OrderRequestListingItem {
    id: string;
    pONumber: string;
    content?: string;
    version?: string;
    airline?: CodedProperty;
    system?: NamedProperty;
    videos: OrderRequestVideos;
    targetAspectRatio?: NamedProperty;
    targetResolution?: OrderResolutionDimensions;
    soundtracks: OrderRequestSoundtracks;
    subtitles: OrderRequestSubtitles;
    dueDate: string;
    startDate: string;
    contentType?: ContentReferenceType;
    status: OrderRequestStatus;
    requestedSubTypes?: DynamicSubtitleType[];
    progress: OrderRequestCompletionProgress;
    targetFilenames: string[];
    statusDetails?: string;
    createdDate: string;
    source: OrderRequestSource;
    updatedDate: string;
    isModified?: boolean;
    isTrailer?: boolean;
    isPinned?: boolean;
    isRevised?: boolean;
    runtime?: string;
}

export enum OrderRequestSource {
    Shreq = 1,
    Buzz = 2
}

type OrderRequestVideos = OrderRequestAssets<OrderRequestVideo>;
type OrderRequestSoundtracks = OrderRequestAssets<OrderRequestSoundtrack[]>;
type OrderRequestSubtitles = OrderRequestAssets<OrderRequestSubtitle[]>;

interface OrderRequestAssets<T> {
    onPrem: T;
    cloud: T;
}

interface OrderRequestVideo {
    targetFilename: string;
    availability: OrderAssetAvailability;
    resolution?: OrderResolutionDimensions;
    nonMatchingVideos: OrderRequestNonMatchingVideo[];
}

interface OrderRequestNonMatchingVideo {
    assetId: string;
    subtitle: CodedProperty;
    aspectRatio: NamedProperty;
    resolution: OrderResolutionDimensions;
}

interface OrderRequestSoundtrack {
    language: LanguageReference;
    availability: OrderAssetAvailability;
}

interface OrderRequestSubtitle {
    language: LanguageReference;
    availability: OrderAssetAvailability;
    isEmbedded: boolean;
    targetFilename?: string;
}

interface OrderRequestCompletionProgress {
    cloud: OrderProgressData;
    onPrem: OrderProgressData;
}

const mapBaseProperties = (
    response: OrderRequestListItemResponse | OrderRequestDetailsResponseItem,
    pinnedItems: string[]
) => ({
    id: response.id,
    pONumber: response.pONumber,
    content: response.content ? resolveOrderContentTitle(response.content) : undefined,
    version: response.content ? resolveOrderContentVersion(response.content) : undefined,
    airline: response.airline,
    system: response.systemPlatform?.system,
    targetResolution: response.targetResolution,
    targetAspectRatio: response.encodeSpecification?.targetAspectRatio,
    requestedSubTypes: response.requestedSubTypes,
    dueDate: response.dueDate,
    status: response.status,
    statusDetails: response.statusDetails,
    startDate: response.startDate,
    updatedDate: response.startDate,
    createdDate: response.audit.createdDate,
    contentType: response.content?.type,
    isTrailer: response.isTrailer,
    source: response.source,
    runtime: response.runtime,
    isRevised: response.isRevised,
    isPinned: pinnedItems.includes(response.id)
});

export const mapListingResponseToOrderRequestListingItem = (
    response: OrderRequestListItemResponse,
    pinnedItems: string[]
): OrderRequestListingItem => {
    const { cloudAssets, onPremAssets } = response;

    const videos = mapVideoAssets(cloudAssets.video, onPremAssets.video);
    const soundtracks = mapSoundtracks(cloudAssets.soundtracks, onPremAssets.soundtracks);
    const subtitles = mapListingSubtitles(cloudAssets.subtitles, onPremAssets.subtitles);

    return {
        ...mapBaseProperties(response, pinnedItems),
        targetFilenames: response.targetFilenames,
        videos,
        soundtracks,
        subtitles,
        progress: {
            cloud: calculateOrderProgress(
                videos.cloud.availability,
                soundtracks.cloud.map((s) => s.availability),
                subtitles.cloud.map((s) => s.availability)
            ),
            onPrem: calculateOrderProgress(
                videos.onPrem.availability,
                soundtracks.onPrem.map((s) => s.availability),
                subtitles.onPrem.map((s) => s.availability)
            )
        }
    };
};

export const mapDetailsResponseToOrderRequestListingItem = (
    response: OrderRequestDetailsResponseItem,
    pinnedItems: string[]
): OrderRequestListingItem => {
    const { cloudAssets, onPremAssets } = response;

    const subtitles = mapDetailedSubtitles(cloudAssets, onPremAssets);
    const targetFilenames = mapTargetFilenames(cloudAssets.video.targetFilename, subtitles.cloud);

    return {
        ...mapBaseProperties(response, pinnedItems),
        targetFilenames,
        videos: mapVideoAssets(cloudAssets.video, onPremAssets.video),
        soundtracks: mapSoundtracks(cloudAssets.soundtracks, onPremAssets.soundtracks),
        progress: {
            cloud: calculateOrderProgress(
                cloudAssets.video.availability,
                cloudAssets.soundtracks.map((s) => s.availability),
                subtitles.cloud.map((s) => s.availability)
            ),
            onPrem: calculateOrderProgress(
                cloudAssets.video.availability,
                cloudAssets.soundtracks.map((s) => s.availability),
                subtitles.cloud.map((s) => s.availability)
            )
        },
        subtitles
    };
};

// target filename mapping
const mapTargetFilenames = (videoTargetFilename: string, cloudSubtitles: OrderRequestSubtitle[]): string[] => {
    const subtitleFilenames = cloudSubtitles
        .map((subtitle) => subtitle.targetFilename)
        .filter((filename): filename is string => !!filename);

    return [videoTargetFilename, ...subtitleFilenames];
};

// video mapping
const mapVideoAssets = (
    cloudVideoResponse: OrderRequestVideoResponse | OrderRequestDetailsVideoResponse,
    onPremVideoResponse: OrderRequestVideoResponse | OrderRequestDetailsVideoResponse
): OrderRequestVideos => {
    return {
        cloud: mapToVideo(cloudVideoResponse),
        onPrem: mapToVideo(onPremVideoResponse)
    };
};

const mapToVideo = (response: OrderRequestVideoResponse | OrderRequestDetailsVideoResponse): OrderRequestVideo => ({
    availability: response.availability,
    resolution: response.resolution,
    targetFilename: response.targetFilename,
    nonMatchingVideos: response.nonMatchingVideos.map((nonMatchingVideo) => ({
        assetId: nonMatchingVideo.assetId,
        subtitle: nonMatchingVideo.subtitle,
        aspectRatio: nonMatchingVideo.aspectRatio,
        resolution: nonMatchingVideo.resolution
    }))
});

// soundtracks mapping
const mapSoundtracks = (
    cloudSoundtracks: Array<OrderRequestSoundtrackResponse | OrderRequestDetailsSubtitleResponse>,
    onPremSoundtracks: Array<OrderRequestSoundtrackResponse | OrderRequestDetailsSubtitleResponse>
): OrderRequestSoundtracks => {
    return {
        cloud: cloudSoundtracks.map(mapToSoundtrack),
        onPrem: onPremSoundtracks.map(mapToSoundtrack)
    };
};

const mapToSoundtrack = (
    response: OrderRequestSoundtrackResponse | OrderRequestDetailsSubtitleResponse
): OrderRequestSoundtrack => ({
    language: response.language,
    availability: response.availability
});

// subtitles mapping
const mapDetailedSubtitles = (
    cloud: OrderRequestDetailsAssetsResponse,
    onPrem: OrderRequestDetailsAssetsResponse
): OrderRequestSubtitles => {
    const cloudSubtitles = uniqBy(cloud.subtitles, (subtitle) => subtitle.language.id).map(mapDetailedSubtitleResponse);

    const onPremSubtitles = uniqBy(onPrem.subtitles, (subtitle) => subtitle.language.id).map(
        mapDetailedSubtitleResponse
    );

    return {
        cloud: addEmbeddedSubtitleIfPresent(cloudSubtitles, cloud.video),
        onPrem: addEmbeddedSubtitleIfPresent(onPremSubtitles, onPrem.video)
    };
};

const addEmbeddedSubtitleIfPresent = (
    subtitles: OrderRequestSubtitle[],
    video: { subtitle?: LanguageReference; availability: OrderAssetAvailability }
): OrderRequestSubtitle[] => {
    if (video.subtitle && video.subtitle?.id !== CLEAN_VIDEO_SUBTITLE_LANGUAGE_ID) {
        subtitles.push({
            language: video.subtitle,
            isEmbedded: true,
            availability: video.availability
        });
    }
    return subtitles;
};

const mapDetailedSubtitleResponse = (subtitle: {
    language: LanguageReference;
    availability: OrderAssetAvailability;
    targetFilename?: string;
}): OrderRequestSubtitle => ({
    language: subtitle.language,
    isEmbedded: false,
    availability: subtitle.availability,
    targetFilename: subtitle.targetFilename
});

const mapListingSubtitles = (
    cloudSubtitles: OrderRequestSubtitleResponse[],
    onPremSubtitles: OrderRequestSubtitleResponse[]
): OrderRequestSubtitles => {
    return {
        cloud: cloudSubtitles.map(mapToSubtitle),
        onPrem: onPremSubtitles.map(mapToSubtitle)
    };
};

const mapToSubtitle = (subtitle: OrderRequestSubtitleResponse): OrderRequestSubtitle => ({
    language: subtitle.language,
    availability: subtitle.availability,
    isEmbedded: subtitle.isEmbedded,
    targetFilename: subtitle.targetFilename
});
